import { FormPanel } from "components/shared-ui/form/form-panel/form-panel";
import { TableSelected } from "components/shared-ui/table/table";
import { CONFIG } from "config/config";
import React, { useEffect, useState } from "react";
import { FormMode, formUtils, IFormField } from "utils/form-utils";
import { IShardRepository } from "api/entities/shard-repository";
import { IShardClusterCreateRequest, IShardClusterDetailsItem } from "api/entities/shard-cluster";
import FormSection from "../../shared-ui/form/form-section/form-section";
import { ShardRepositoryPanel, ShardRepositoryPanelMode } from "../../shard-repositories/panels/shard-repository-panel/shard-repository-panel";
import { IShardRepositoryTypes } from "../../shard-repositories/sections/shard-repository-section/shard-repository-section";
import { SelectShardRepositoriesPanel } from "../panels/select-shard-repositories-panel/select-shard-repositories-panel";
import { PoolNameSection } from "../sections/pool-name-section/pool-name-section";
import { IShardClusterRepository, ShardRepositoriesSection, } from "../sections/storage-nodes-section/shard-repositories-section";
import { StorageReconstructionSection } from "../sections/storage-reconstruction-section/storage-reconstruction-section";
import { IShardClusterRepositoryCredentialStatus, ShardClusterCredentialsUpdatePanel, } from "../panels/shard-cluster-credentials-update-panel/shard-cluster-credentials-update-panel";
import { useApi } from "utils/hooks/use-api";
import { jobService } from "api/services/job-service";
import { IClusterDeploymentSbConfig, K8Vendor, } from "api/entities/deployment-job";
import { bZsConfigExportUtils } from "utils/bzs-config-export-utils";
import { clusterCredentialService } from "api/services/cluster-credential-service";
import { AccessCodesGeneratedDialog } from "components/access-credentials/dialogs/access-codes-generated-dialog/access-codes-generated-dialog";
import { IAccessCredentialCreateResponse } from "api/entities/access-credential";
import styles from "./shard-cluster-form.module.scss";
import { shardClusterService } from "../../../api/services/shard-cluster-service";
import { accountService } from "../../../api/services/account-service.ts";
import { ThunkDispatch } from "redux-thunk";
import { IAppState } from "../../../redux/reducers";
import { Action } from "redux";
import { shardClusterThunkActions } from "../../../redux/actions/shard-cluster.ts";
import { tooltipUserGuideActions } from "../../../redux/actions/tooltip-user-guide.ts";
import { connect } from "react-redux";

export interface IShardClusterFormProps
  extends ReturnType<typeof mapDispatchToProps> {

  storagePoolId?: string;
  form: IShardClusterForm;
  mode: FormMode;
}

export interface IShardClusterFormValue {
  name: string;
  shardClusterRepositories: IShardClusterRepository[];
  contentSecurityLevel: number;
  keySecurityLevel: number;
  metaSecurityLevel: number;
}

export interface IShardClusterForm {
  name: IFormField<string>;
  shardClusterRepositories: IFormField<IShardClusterRepository[]>;
  contentSecurityLevel: IFormField<number>;
  keySecurityLevel: IFormField<number>;
  metaSecurityLevel: IFormField<number>;
}

export const shardClusterFormDefaultValue: IShardClusterFormValue = {
  name: "",
  shardClusterRepositories: [],
  contentSecurityLevel: CONFIG.minSecurityLevel,
  keySecurityLevel: CONFIG.minSecurityLevel,
  metaSecurityLevel: CONFIG.minSecurityLevel,
};

type IdType = number | string;

function ShardClusterForm(props: IShardClusterFormProps) {
  const { form } = props;

  const [
    isShardClusterCredentialsUpdatePanelOpen,
    setIsShardClusterCredentialsUpdatePanelOpen,
  ] = useState<boolean>(false);

  const [shardRepositoryPanelMode, setShardRepositoryPanelMode] =
    useState<ShardRepositoryPanelMode>(null);
  const [selectedShardRepository, setSelectedShardRepository] =
    useState<IShardClusterRepository>(null);

  const [lastAddedShardRepository, setLastAddedShardRepository] =
    useState<IShardRepository>(null);
  const [selectShardRepositoryPanelOpen, setSelectShardRepositoryPanelOpen] =
    useState<boolean>(false);

  const [selection, setSelection] = useState<TableSelected<IShardRepository>>({});

  const [shardClusterApi] = useApi();
  const [credentialsApi] = useApi();
  const [deploymentApi] = useApi();

  const [
    isAccessCodesGeneratedDialogOpen,
    setIsAccessCodesGeneratedDialogOpen,
  ] = useState<boolean>(false);
  const [savedAccessCredentials, setSavedAccessCredentials] =
    useState<IAccessCredentialCreateResponse>(null);

  const [shardCluster, setShardCluster] =
    useState<IShardClusterDetailsItem>(null);

  const [nodesCount, setNodesCount] = useState({
    content: CONFIG.minSecurityLevel,
    key: CONFIG.minSecurityLevel,
    meta: CONFIG.minSecurityLevel,
  });
  const formError = formUtils.getFormError(form);

  function handleEditStorageNode(poolNode: IShardClusterRepository) {
    setShardRepositoryPanelMode(
      props.mode === FormMode.Create
        ? ShardRepositoryPanelMode.FullEdit
        : ShardRepositoryPanelMode.LimitedEdit
    );
    setSelectedShardRepository(poolNode);
  }

  const handleAddShardRepository = () =>
    setShardRepositoryPanelMode(ShardRepositoryPanelMode.Add);

  const handleCloseShardRepositoryPanel = () => {
    setShardRepositoryPanelMode(null);
    setSelectedShardRepository(null);
  };

  function handleOpenSelectShardRepositoryPanel() {
    const repositories = form.shardClusterRepositories.value;
    let initialSelection = {};

    for (let i = 0; i < repositories.length; i++) {
      initialSelection[repositories[i].shardRepository.id] = repositories[i].shardRepository;
    }

    setSelectShardRepositoryPanelOpen(true);
    if (repositories.length > 0) {
      setTimeout(() => {
        setSelection(initialSelection);
      }, 0);
    }
  }

  async function closeShardClusterCredentialsUpdatePanelAndSaveAccessCredentials(
    shardClusterRepositories?: IShardClusterRepositoryCredentialStatus[]
  ) {
    if (!shardClusterRepositories)
      return;

    setIsShardClusterCredentialsUpdatePanelOpen(false);

    try {
      const creds = await saveClusterCredentials();

      const nbConfig = {
        roleCredentials: [
          {
            accessKey: creds.accessKey,
            secret: creds.accessSecret,
            role: "",
          },
        ],
      };

      const sbConfig: IClusterDeploymentSbConfig[] = await Promise.all(
        shardClusterRepositories.map(async (node) => {
          const details = await bZsConfigExportUtils.getStorageNodeDetails(
            node.shardRepositoryDetails
          );
          return {
            id: node.shardRepositoryDetails.id,
            data: details.data,
          };
        })
      );

      await deploymentApi(
        jobService.createCluster({
          applicationId: creds.applicationId,
          storagePoolId: shardCluster.id,
          label: form.name.value,
          region: "us-iad",
          vendor: K8Vendor.Linode,
          nbConfig,
          sbConfig,
        })
      );

      setSavedAccessCredentials({
        accessKey: creds.accessKey,
        accessSecret: creds.accessSecret,
        name: creds.name,
        bucketId: "",
        id: creds.id,
        scope: creds.scope,
      });
      setIsAccessCodesGeneratedDialogOpen(true);

      //setStoragePoolNodes(shardClusterRepositories);
    } catch (error) {
      console.error("Failed to save credentials or create cluster:", error);
      // Optional: Show error to the user
    }
  }

  function saveClusterCredentials() {

    return credentialsApi(
      clusterCredentialService.create({
        name: `${form.name.value}-cluster-cred`,
        scope: "full",
        applicationName: `${form.name.value}-app-FULL`,
      })
    );
  }
  function handleCloseSelectStorageNodesPanel() {
    setSelectShardRepositoryPanelOpen(false);

    let nodes = form.shardClusterRepositories.value;
    const selectedShardRepositoryIds = Object.values(selection).map((x) => x.id);
    const currentStorageNodeIds = nodes.map((x) => x.shardRepository.id);

    nodes = nodes.filter((x) =>
      selectedShardRepositoryIds.includes(String(x.shardRepository.id))
    );
    const newShardRepositoryIds = subtract(
      selectedShardRepositoryIds,
      currentStorageNodeIds
    );

    const types = {
      storeContent: true,
      storeKey: true,
      storeMetadata: true,
    };

    const newShardRepositories = newShardRepositoryIds.map((x) => ({
      shardRepository: selection[x],
      types: Object.assign({}, types),
    }));
    form.shardClusterRepositories.onChange([...nodes, ...newShardRepositories]);
  }

  function subtract(a: IdType[], b: IdType[]): IdType[] {
    let setB = new Set(b);
    let res: Set<IdType> = new Set();
    for (let i = 0; i < a.length; i++) {
      if (!setB.has(a[i])) {
        res.add(a[i]);
      }
    }
    return Array.from(res);
  }

  function handleStorageNodeSaved(
    shardRepository: IShardRepository,
    shardRepositoryTypes: IShardRepositoryTypes
  ) {
    const nodes = form.shardClusterRepositories.value;
    if (
      shardRepositoryPanelMode === ShardRepositoryPanelMode.FullEdit ||
      shardRepositoryPanelMode === ShardRepositoryPanelMode.LimitedEdit
    ) {
      nodes[nodes.indexOf(selectedShardRepository)] = {
        shardRepository: shardRepository,
        types: shardRepositoryTypes,
      };
      form.shardClusterRepositories.onChange([...nodes]);
    } else if (shardRepositoryPanelMode === ShardRepositoryPanelMode.Add) {
      setLastAddedShardRepository(shardRepository);
      form.shardClusterRepositories.onChange([
        ...nodes,
        { shardRepository: shardRepository, types: shardRepositoryTypes },
      ]);
    }
  }

  function save() {
    formUtils.validateAll(form).then((isValid) => {
      if (isValid) {
        const formValue = formUtils.getFormValue<IShardClusterFormValue>(form);
        if (props.mode === FormMode.Create) {
          const getStorageNodeIds = (
            list: IShardClusterRepository[],
            storeTypeKey: keyof IShardRepositoryTypes
          ) => {
            return list
              .filter((n) => n.types[storeTypeKey])
              .map((n) => n.shardRepository.id);
          };

          let createRequest: IShardClusterCreateRequest = {
            name: formValue.name,
            ContentSecurityLevel: formValue.contentSecurityLevel,
            KeySecurityLevel: formValue.keySecurityLevel,
            MetaSecurityLevel: formValue.metaSecurityLevel,
            contentStorages: getStorageNodeIds(
              formValue.shardClusterRepositories,
              "storeContent"
            ),
            keyStorages: getStorageNodeIds(
              formValue.shardClusterRepositories,
              "storeKey"
            ),
            metaStorages: getStorageNodeIds(
              formValue.shardClusterRepositories,
              "storeMetadata"
            ),
          };

          shardClusterApi(
            shardClusterService.addShardCluster(createRequest)
          ).then((cluster) => {
            props.fetchPage();

            shardClusterService.get(cluster.id).then((poolDetails) => {
              setShardCluster(poolDetails);
              setIsShardClusterCredentialsUpdatePanelOpen(true);
            });
          });
          // TODO: navigate to creds / create creds
        } else if (props.mode === FormMode.Edit) {

          if (form.name.isChanged) {
            shardClusterApi(shardClusterService.update({
              id: props.storagePoolId,
              name: formValue.name,
            }));
          }

        }
      }
    });
  }

  function closeAccessCodesGeneratedDialog() {
    setIsAccessCodesGeneratedDialogOpen(false);
  }

  useEffect(() => {
    const nodes = form.shardClusterRepositories.value;
    const _nodesCount: typeof nodesCount = {
      content: nodes.filter((node) => node.types.storeContent).length,
      key: nodes.filter((node) => node.types.storeKey).length,
      meta: nodes.filter((node) => node.types.storeMetadata).length,
    };

    function updateSecurityLevel(
      securityLevelField: IFormField<number>,
      nodesCount: number
    ) {
      const updatedValue = Math.max(
        Math.min(securityLevelField.value, nodesCount - 1),
        CONFIG.minSecurityLevel
      );
      if (updatedValue !== securityLevelField.value) {
        securityLevelField.onChange(updatedValue);
      }
    }

    updateSecurityLevel(form.contentSecurityLevel, _nodesCount.content);
    updateSecurityLevel(form.keySecurityLevel, _nodesCount.key);
    updateSecurityLevel(form.metaSecurityLevel, _nodesCount.meta);

    setNodesCount(_nodesCount);

    // eslint-disable-next-line
  }, [form.shardClusterRepositories.value]);

  useEffect(() => {
    form.shardClusterRepositories.validate(form.shardClusterRepositories.value);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <FormPanel
        className={styles.root}
        okButtonText={FormMode.Create ? "Next" : "Save"}
        okButtonClick={save}
        okButtonDisabled={!!formError}
        errorMessage={formError}
      >
        <PoolNameSection name={form.name} />
        <ShardRepositoriesSection
          mode={props.mode}
          shardClusterRepositories={form.shardClusterRepositories}
          onOpenSelectStorageNodesPanel={handleOpenSelectShardRepositoryPanel}
          onEditStorageNode={handleEditStorageNode}
        />
        <StorageReconstructionSection
          isReadOnly={props.mode === FormMode.Edit}
          contentSecurityLevel={form.contentSecurityLevel}
          keySecurityLevel={form.keySecurityLevel}
          metaSecurityLevel={form.metaSecurityLevel}
          maxContentSecurityLevel={Math.max(
            nodesCount.content - 1,
            CONFIG.minSecurityLevel
          )}
          maxKeySecurityLevel={Math.max(
            nodesCount.key - 1,
            CONFIG.minSecurityLevel
          )}
          maxMetaSecurityLevel={Math.max(
            nodesCount.meta - 1,
            CONFIG.minSecurityLevel
          )}
          contentRedundancyLevel={nodesCount.content}
          keyRedundancyLevel={nodesCount.key}
          metaRedundancyLevel={nodesCount.meta}
        />
        {/* TODO: add deployment status and region */}
        {props.mode === FormMode.Edit && (
          <>
            <FormSection title="Air Gap">
              <div
                data-testid="air-gap"
                className={styles["air-gap--readonly"]}
              >
                Enabled
              </div>
            </FormSection>

            <FormSection title="Immutability">
              <div
                data-testid="immutability"
                className={styles["immutability--readonly"]}
              >
                Transparently Enabled
              </div>
            </FormSection>
          </>
        )}
      </FormPanel>

      <SelectShardRepositoriesPanel
        open={selectShardRepositoryPanelOpen}
        onOpenShardRepositoryPanel={handleAddShardRepository}
        onClose={handleCloseSelectStorageNodesPanel}
        lastAddedShardRepository={lastAddedShardRepository}
        selection={selection}
        setSelection={setSelection}
      />

      <ShardRepositoryPanel
        mode={shardRepositoryPanelMode}
        shardClusterRepository={selectedShardRepository}
        onClose={handleCloseShardRepositoryPanel}
        onRepositorySaved={handleStorageNodeSaved}
      />

      <ShardClusterCredentialsUpdatePanel
        open={isShardClusterCredentialsUpdatePanelOpen}
        onClose={() => {
          setIsShardClusterCredentialsUpdatePanelOpen(false);

          if (accountService.getTooltipUserGuideState().showTooltip) {
            props.shardClusterTooltipOpen();
          }
        }}
        onSaveShardClusterRepositories={
          closeShardClusterCredentialsUpdatePanelAndSaveAccessCredentials
        }
        shardCluster={shardCluster}
        accessCredentialsScope={"full"}
      />

      <AccessCodesGeneratedDialog
        open={isAccessCodesGeneratedDialogOpen}
        url={shardCluster && `s3.${shardCluster.name}.bucketzero.cloud`}
        applicationCredentials={savedAccessCredentials}
        onClose={() => closeAccessCodesGeneratedDialog()}
      />
    </>
  );
}

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
  return {
    fetchPage: () => dispatch(shardClusterThunkActions.entity.fetchPage()),

    shardClusterTooltipOpen: () => {
      dispatch(tooltipUserGuideActions.disableStepCounter());
      dispatch(tooltipUserGuideActions.open());
      dispatch(tooltipUserGuideActions.setStep("bucket"));
    },
  };
}

export default connect(null, mapDispatchToProps)(ShardClusterForm);
